import { useQuery } from "@apollo/client";
import { makeStyles } from "@mui/styles";
import pluralize from "pluralize";

import Button from "@mui/material/Button";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { displayVisitStatus } from "~/emr/utils/displayUtils";
import {
  COLOR_KICK_BLUE,
  COLOR_KICK_LIGHT_BLUE,
  COLOR_KICK_LIGHT_TAN,
  COLOR_KICK_LIGHT_YELLOW,
  COLOR_KICK_NAVY,
  COLOR_KICK_SLATE,
  DB_STATUS_ACTIVE,
  DB_STATUS_CANCELED,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_READY,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
} from "~/shared/sharedConstants";
import {
  preferLiveSubscription,
  subscriptionGrantsFollowUpAccess,
} from "~/shared/utils/sharedUtils";
import {
  clearActiveSubscriptionsBySubType,
  clearVisitCookies,
  setActiveSubscriptionBySubType,
} from "~/visits/utils/visitCookies";
import { setCookiesAndRedirectToInsomniaFollowUpVisit } from "~/visits/utils/visitUtils";
import {
  FILL_REQUEST_DEAD_STATUSES,
  subscriptionNextShipment,
  subscriptionPauseState,
  subscriptionShipmentStatus,
  subscriptionStatusChip,
} from "~/patient-portal/utils/treatmentCard";
import imgSleep from "~assets/images/visits/sleep-bottles.jpg";
import { GET_PATIENT_RECORD } from "./portalQueries";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}));

const subscriptionItemStyle = {
  backgroundColor: "white",
  padding: "24px",
  width: "100%",
  margin: "0 0 16px",
  borderRadius: "12px",
  boxShadow:
    "0px 1px 2px rgba(30, 35, 71, 0.06), 0px 8px 24px rgba(30, 35, 71, 0.08)",
  maxWidth: "800px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

// Tone → pill color for the at-a-glance status chip. "danger" reuses the red already used
// elsewhere on the card; the rest are subtle status colors.
const STATUS_CHIP_COLORS = {
  warning: "#d69e2e",
  danger: "#e53e3e",
  neutral: "#718096",
};

// StatusChip renders the consolidated subscription status pill (Paused · resumes … /
// Cancels … / Canceled) beside the membership title, or nothing when there's no meaningful
// state — a plainly active subscription shows no chip.
function StatusChip({ subscription }) {
  const { label, tone } = subscriptionStatusChip(subscription);
  if (!label) {
    return null;
  }
  return (
    <span
      style={{
        color: "white",
        backgroundColor: STATUS_CHIP_COLORS[tone] || STATUS_CHIP_COLORS.neutral,
        fontSize: "13px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "12px",
        whiteSpace: "nowrap",
        alignSelf: "center",
      }}
    >
      {label}
    </span>
  );
}

export function prescriptionWithFillRequests(prescription, fillRequests) {
  let refillsRemaining = prescription.refills - prescription.numRefillsFilled;
  refillsRemaining = Math.min(refillsRemaining, prescription.refills);
  const rxWithFill = {
    ...prescription,
    fillRequests: fillRequests.filter(
      (fillRequest) =>
        fillRequest.doctorPrescription1ID === prescription.id ||
        fillRequest.doctorPrescription2ID === prescription.id ||
        fillRequest.doctorPrescription3ID === prescription.id ||
        fillRequest.doctorPrescription4ID === prescription.id,
    ),
    refillsRemaining,
  };
  // Pick the most recent fill request that isn't canceled/failed — a canceled or
  // errored fill must never drive the "Last sent"/"Pending shipment" label or mask
  // an earlier real shipment. Mirrors the treatment card's dead-status handling.
  rxWithFill.mostRecentFillRequest = rxWithFill.fillRequests
    .filter(
      (fillRequest) => !FILL_REQUEST_DEAD_STATUSES.includes(fillRequest.status),
    )
    .reduce(
      (mostRecent, fillRequest) =>
        mostRecent.createdAt > fillRequest.createdAt ? mostRecent : fillRequest,
      { createdAt: 0 },
    );

  return rxWithFill;
}

function hasFirstPendingSubVisit(data) {
  if (!data?.patientRecord?.visits?.length) {
    return false;
  }
  // sort visits by visitDate having the most recent visit first
  const sortedVisits = [...data.patientRecord.visits].sort(
    (a, b) => new Date(b.visitDate) - new Date(a.visitDate),
  );
  for (let visit of sortedVisits) {
    if (
      visit.visitType === "IN" &&
      !displayVisitStatus(visit.status).includes("Closed")
    ) {
      return true;
    }
  }
}

function hasPendingFollowUpVisit(latestVisit) {
  return (
    latestVisit.visitType === VISIT_TYPE_INSOMNIA_FOLLOW_UP &&
    [
      DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
      DOCTOR_VISIT_STATUS_PENDING,
      DOCTOR_VISIT_STATUS_READY,
      DOCTOR_VISIT_STATUS_AWAITING_REPLY,
    ].includes(latestVisit.status)
  );
}

// Muted secondary text used throughout the card (quantities, directions, status lines).
const MUTED_TEXT_COLOR = "#718096";

// Darker companion to STATUS_CHIP_COLORS.warning, used for TEXT in warning treatments — the chip
// amber doesn't have enough contrast on light backgrounds at body sizes.
const WARNING_TEXT_COLOR = "#8a6d1d";

// Small uppercase label that gives the card a scannable section structure.
const sectionLabelStyle = {
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: COLOR_KICK_SLATE,
  marginBottom: "8px",
};

// quantityText renders a friendly "30 gummies" / "1 tablet" from the prescription's quantity and
// form factor (the old hardcoded "s" produced "30 Gummys" and "1 Tablets").
function quantityText(rxWithFill) {
  if (!rxWithFill.medicationFormFactor) {
    return `${rxWithFill.quantity}`;
  }
  return `${rxWithFill.quantity} ${pluralize(
    rxWithFill.medicationFormFactor.toLowerCase(),
    rxWithFill.quantity,
  )}`;
}

// MedicationRow is one prescription on the card: what it is and how much. Shipment status lives
// at the card level (one line per pharmacy fill request, since bundled meds ship together); only
// the out-of-refills note stays per-medication (autoship only, as before).
function MedicationRow({ rxWithFill, idx }) {
  const isAutoship = rxWithFill.autoshipStatus === DB_STATUS_ACTIVE;
  return (
    <div
      style={{
        padding: idx ? "14px 0" : "0 0 14px",
        borderTop: idx ? "1px solid #f0f0f0" : "none",
      }}
    >
      <div
        style={{ fontSize: "16px", fontWeight: 600, color: COLOR_KICK_NAVY }}
      >
        {rxWithFill.medicationName} {rxWithFill.medicationDosage}
      </div>
      <div
        style={{ fontSize: "14px", color: MUTED_TEXT_COLOR, marginTop: "2px" }}
      >
        {quantityText(rxWithFill)}
      </div>
      {isAutoship && !rxWithFill.refillsRemaining && (
        <div
          style={{
            fontSize: "14px",
            color: WARNING_TEXT_COLOR,
            marginTop: "8px",
          }}
        >
          No refills remaining. Please complete a follow-up visit for a new
          prescription.
        </div>
      )}
    </div>
  );
}

// NextShipmentStrip is the single most important answer on the card — when medication arrives
// next — so it gets its own tinted strip instead of blending into the text. Neutral tone is a
// date promise; warning means the prescription lapses first and a follow-up is needed.
function NextShipmentStrip({ shipment }) {
  const isWarning = shipment.tone === "warning";
  const Icon = isWarning ? WarningAmberRoundedIcon : LocalShippingOutlinedIcon;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        backgroundColor: isWarning
          ? COLOR_KICK_LIGHT_YELLOW
          : COLOR_KICK_LIGHT_BLUE,
        borderRadius: "8px",
        padding: "12px 14px",
        marginTop: "14px",
      }}
    >
      <Icon
        style={{
          color: isWarning ? STATUS_CHIP_COLORS.warning : COLOR_KICK_BLUE,
          fontSize: "20px",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: "14px",
          fontWeight: 600,
          lineHeight: 1.45,
          color: isWarning ? WARNING_TEXT_COLOR : COLOR_KICK_NAVY,
        }}
      >
        {shipment.label}
      </span>
    </div>
  );
}

const PortalMySubscriptions = () => {
  const classes = useStyles();
  const { data } = useQuery(GET_PATIENT_RECORD);
  const subscriptionsBySubType = {};

  clearActiveSubscriptionsBySubType();

  // create a map of subscriptions grouped by subType, keeping the most "live" row per subType
  // (active > paused > canceled — see preferLiveSubscription). The feeding query has no ORDER BY,
  // so this must be stable regardless of row order: an active row can never be displaced by a
  // paused or canceled one.
  (data?.patientRecord?.subscriptions || []).forEach((subscription) => {
    subscriptionsBySubType[subscription.subType] = preferLiveSubscription(
      subscriptionsBySubType[subscription.subType],
      subscription,
    );
  });
  // Cache each subType's winning subscription for the follow-up flow when it grants access
  // (active, or paused but within the paid-through window) so the follow-up is treated as
  // covered, not a new purchase.
  Object.values(subscriptionsBySubType).forEach((subscription) => {
    if (subscriptionGrantsFollowUpAccess(subscription)) {
      setActiveSubscriptionBySubType(subscription.subType, subscription);
    }
  });

  return (
    <main className={classes.content}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          My Subscriptions
        </h3>
        {Object.values(subscriptionsBySubType).map((subscription) => {
          if (!subscription.prescriptions) {
            return null;
          }
          const hasRefillsRemaining = subscription.prescriptions.some(
            (rx) => rx.numRefillsFilled < rx.refills,
          );
          const hasAutoship = subscription.prescriptions.some(
            (rx) => rx.autoshipStatus === DB_STATUS_ACTIVE,
          );
          const hasPrescriptions = subscription.prescriptions.length > 0;
          // Card-level next-shipment line (pause- and rx-expiration-aware); null when there's
          // nothing truthful to promise. Replaces the old per-medication "Next shipment" rows,
          // which all carried the same subscription-wide date anyway.
          const nextShipment = subscriptionNextShipment(subscription);
          const rxsWithFill = subscription.prescriptions.map((prescription) =>
            prescriptionWithFillRequests(
              prescription,
              subscription.fillRequests,
            ),
          );
          // Shipment status, deduped by fill request: meds bundled into one pharmacy fill
          // request share a single "Shipped … · Track shipment" line instead of repeating it per
          // medication; distinct fill requests (rare) each keep their own line. Visit-only
          // non-autoship orders have no shipment to report (same gate the per-med lines used).
          const seenShipments = new Set();
          const shipmentLines = rxsWithFill
            .filter(
              (rx) =>
                rx.autoshipStatus === DB_STATUS_ACTIVE ||
                !subscription.latestVisit?.orderType?.includes("visit-only"),
            )
            .map((rx) => subscriptionShipmentStatus(rx))
            .filter((shipment) => {
              if (!shipment.label) {
                return false;
              }
              const key = `${shipment.label}|${shipment.trackingURL}`;
              if (seenShipments.has(key)) {
                return false;
              }
              seenShipments.add(key);
              return true;
            });
          // The action panel's contextual one-liner (same conditions as the old centered copy,
          // computed up front so an empty message renders nothing).
          const actionMessage =
            subscription.status === DB_STATUS_CANCELED
              ? "Your subscription has been canceled. Complete a follow-up visit to restart your subscription."
              : hasPendingFollowUpVisit(subscription.latestVisit)
                ? "Your follow-up visit is currently under review by one of our doctors."
                : hasRefillsRemaining && hasPrescriptions
                  ? hasAutoship
                    ? "Want to update something or try a new medication? Your subscription allows for unlimited follow-up visits."
                    : "How is your medication trial going?\nComplete a follow-up visit any time to adjust medication or set up automatic refill shipments."
                  : "";

          return (
            <div
              key={subscription.id}
              style={{
                backgroundColor: "white",
                padding: "24px",
                width: "100%",
                margin: "0 0 16px",
                borderRadius: "12px",
                boxShadow:
                  "0px 1px 2px rgba(30, 35, 71, 0.06), 0px 8px 24px rgba(30, 35, 71, 0.08)",
                maxWidth: "800px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "16px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      marginRight: "14px",
                      objectFit: "cover",
                    }}
                    src={imgSleep}
                    alt=""
                  />
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: COLOR_KICK_NAVY,
                      margin: "0",
                    }}
                  >
                    Sleep Membership
                  </h3>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <StatusChip subscription={subscription} />
                </div>
              </div>

              {!hasPrescriptions && (
                <div
                  style={{
                    fontSize: "15px",
                    color: MUTED_TEXT_COLOR,
                    padding: "4px 0 8px",
                  }}
                >
                  Your visit will be reviewed by one of our doctors soon.
                </div>
              )}

              {hasPrescriptions && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                  {/* Left: what the patient is taking and when it arrives. */}
                  <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                    <div style={sectionLabelStyle}>
                      {subscription.prescriptions.length === 1
                        ? "Your medication"
                        : "Your medications"}
                    </div>
                    {rxsWithFill.map((rxWithFill, idx) => (
                      <MedicationRow
                        key={rxWithFill.id}
                        rxWithFill={rxWithFill}
                        idx={idx}
                      />
                    ))}
                    {shipmentLines.length > 0 && (
                      <div
                        style={{
                          borderTop: "1px solid #f0f0f0",
                          paddingTop: "14px",
                        }}
                      >
                        <div style={sectionLabelStyle}>Shipment status</div>
                        {shipmentLines.map((shipment) => (
                          <div
                            key={`${shipment.label}|${shipment.trackingURL}`}
                            style={{
                              fontSize: "14px",
                              color: MUTED_TEXT_COLOR,
                              marginBottom: "4px",
                            }}
                          >
                            {shipment.label}
                            {shipment.trackingURL && (
                              <>
                                {" · "}
                                <a
                                  href={shipment.trackingURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: COLOR_KICK_BLUE,
                                    fontWeight: 600,
                                  }}
                                >
                                  Track shipment
                                </a>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {nextShipment && (
                      <NextShipmentStrip shipment={nextShipment} />
                    )}
                  </div>

                  {/* Right: what the patient can do about it. */}
                  <div
                    style={{
                      flex: "1 1 280px",
                      backgroundColor: COLOR_KICK_LIGHT_TAN,
                      borderRadius: "10px",
                      padding: "20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    {actionMessage && (
                      <div
                        style={{
                          maxWidth: "320px",
                          fontSize: "14px",
                          color: MUTED_TEXT_COLOR,
                          lineHeight: 1.5,
                          marginBottom: "14px",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {actionMessage}
                      </div>
                    )}
                    <Button
                      color="primary"
                      size="large"
                      style={{
                        backgroundColor: COLOR_KICK_BLUE,
                        color: "white",
                        width: "100%",
                        maxWidth: "300px",
                        borderRadius: "8px",
                        fontWeight: 400,
                      }}
                      onClick={async () => {
                        // A scheduled pause (still inside the paid period) doesn't gate
                        // follow-ups: the visit is covered by the last paid invoice and the
                        // pause stays in place, so it flows exactly like an active membership.
                        // Only a functionally paused membership requires confirming the
                        // resume-and-charge restart.
                        if (
                          subscriptionPauseState(subscription) === "paused" &&
                          !confirm(
                            "Completing a follow-up visit will resume your subscription and you'll be charged to restart it. Proceed?",
                          )
                        ) {
                          return;
                        }
                        // TODO: dont hardcode to insomnia
                        setCookiesAndRedirectToInsomniaFollowUpVisit(
                          data.patientRecord.patient,
                          subscription.latestVisit,
                        );
                      }}
                    >
                      Follow-up Visit&nbsp;
                      <NavigateNextIcon />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {hasFirstPendingSubVisit(data) && (
          <div
            style={{
              ...subscriptionItemStyle,
            }}
          >
            <h5>
              Thank you for completing an insomnia visit. It is currently under
              review by one our doctors.
            </h5>
          </div>
        )}

        {data &&
          !data.patientRecord?.subscriptions?.length &&
          !hasFirstPendingSubVisit(data) && (
            <div
              style={{
                ...subscriptionItemStyle,
              }}
            >
              <h5>No subscriptions at the moment</h5>
              <Button
                onClick={() => {
                  clearVisitCookies();
                  window.location.href = `/visits/inc/1`;
                }}
                color="primary"
                size="large"
                style={{
                  backgroundColor: COLOR_KICK_BLUE,
                  color: "white",
                  width: "300px",
                  margin: "20px 0",
                }}
              >
                Start Insomnia Visit&nbsp;
                <NavigateNextIcon />
              </Button>
            </div>
          )}
      </div>
    </main>
  );
};

export default PortalMySubscriptions;
